import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Spinner, Select, Input } from '../ui.jsx';
import { Safe } from '../widgets/ErrorBoundary.jsx';

// Lead Sources analytics — answers "which channel is actually paying for itself?"
// For each source: leads, booking rate, win rate, revenue, $/lead, $/booking.
// Sorted by revenue desc so the best-performing source is always at the top.
//
// Source attribution comes from contacts.source which gets populated by the
// /api/lead-capture/in?source=X query param + UTM body fields + Referer header.
// So owners who give different webhook URLs (?source=Google%20Ads vs
// ?source=Facebook) to different platforms see the breakdown automatically.

const PRESETS = [
  { id:'30d', label:'30 days', days:30 },
  { id:'90d', label:'90 days', days:90 },
  { id:'mtd', label:'Month to date' },
  { id:'ytd', label:'Year to date' },
  { id:'all', label:'All time', days: 36500 },
];

export function LeadSources({ toast }) {
  const [preset, setPreset] = useState('90d');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const range = (() => {
    if (from && to) return { from, to };
    const now = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    const p = PRESETS.find(x => x.id === preset);
    if (p?.days) return { from: iso(new Date(now.getTime() - p.days * 86400000)), to: iso(now) };
    if (preset === 'mtd') return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) };
    if (preset === 'ytd') return { from: iso(new Date(now.getFullYear(), 0, 1)), to: iso(now) };
    return { from: iso(new Date(now.getTime() - 90 * 86400000)), to: iso(now) };
  })();

  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api(`/api/contacts/lead-sources?from=${range.from}&to=${range.to}`).then(setData).catch(e=>toast?.(e.message,'error'));
  }, [range.from, range.to]);

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18, gap:14, flexWrap:'wrap' }}>
      <div>
        <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Lead Sources</h1>
        <div style={{ color:G.textMuted, fontSize:13, marginTop:4 }}>
          Which channels are actually paying for themselves. Ranked by revenue.
        </div>
      </div>
      <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
        <Select value={preset} onChange={e=>{ setPreset(e.target.value); setFrom(''); setTo(''); }} style={{ maxWidth:180 }}>
          {PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          <option value="custom">Custom range</option>
        </Select>
        {preset === 'custom' && <>
          <Input type="date" value={from} onChange={e=>setFrom(e.target.value)}/>
          <Input type="date" value={to} onChange={e=>setTo(e.target.value)}/>
        </>}
      </div>
    </div>

    <Safe name="LeadSourcesBody">
      {!data ? <Spinner sz={32}/> : <Body data={data}/>}
    </Safe>
  </div>;
}

function Body({ data }) {
  const t = data.totals;
  const Tile = ({ label, value, sub, color }) => (
    <div style={{ ...styles.card, padding:14, borderColor: (color||G.cardBorder)+'66' }}>
      <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
      <div style={{ fontSize:22, fontWeight:800, marginTop:4, color: color || G.text }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>{sub}</div>}
    </div>
  );

  return <>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:10, marginBottom:18 }}>
      <Tile label="Sources" value={t.sourceCount}/>
      <Tile label="Total leads" value={t.leads}/>
      <Tile label="Booked" value={t.booked} sub={`${t.leads ? Math.round((t.booked/t.leads)*100) : 0}% booking rate`} color={G.purple}/>
      <Tile label="Won (paid)" value={t.won} color={G.green}/>
      <Tile label="Revenue" value={fmtUsd(t.revenue)} color={G.green}/>
    </div>

    {data.sources.length === 0 ? <div style={{ ...styles.card, padding:40, textAlign:'center', color:G.textMuted }}>
      No leads in this window. If your sources are sending to the webhook, double-check the URL has a <code>?source=...</code> query param so attribution works.
    </div> : <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
        <thead><tr style={{ background:G.input }}>
          {['Source','Leads','Booked','Won','Booking %','Win %','Revenue','$ / Lead','$ / Booking'].map(h => (
            <th key={h} style={{ ...styles.th, padding:'10px 12px' }}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {data.sources.map((s, i) => (
            <tr key={s.source}>
              <td style={{ ...styles.td, fontWeight:700 }}>
                {i === 0 && s.revenue > 0 && <span style={{ marginRight:6 }}>🏆</span>}
                {s.source}
              </td>
              <td style={styles.td}>{s.leads}</td>
              <td style={styles.td}>{s.booked}</td>
              <td style={styles.td}>{s.won}</td>
              <td style={styles.td}>
                <Badge c={s.bookingRate>=30?G.green:s.bookingRate>=15?G.yellow:G.red}
                  bg={(s.bookingRate>=30?G.green:s.bookingRate>=15?G.yellow:G.red)+'22'}>
                  {s.bookingRate}%
                </Badge>
              </td>
              <td style={styles.td}>{s.winRate}%</td>
              <td style={{ ...styles.td, fontWeight:700, color: s.revenue > 0 ? G.green : G.textMuted }}>{fmtUsd(s.revenue)}</td>
              <td style={styles.td}>{fmtUsd(s.avgRevenuePerLead)}</td>
              <td style={styles.td}>{fmtUsd(s.avgRevenuePerBooking)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>}

    <div style={{ marginTop:18, padding:14, background:G.input, borderRadius:8, fontSize:12, color:G.textMuted, lineHeight:1.6 }}>
      <strong style={{ color:G.text }}>💡 How attribution works:</strong> Source is set from the webhook URL's <code>?source=...</code> query param (highest priority), then <code>utm_source</code> body field, then the HTTP <code>Referer</code> header, then "Unknown Webhook" as fallback. To split traffic by channel, give each platform a webhook URL with a different <code>?source=</code>: e.g., <code>?source=Google%20Ads</code> for Google Ads forms, <code>?source=Facebook</code> for FB lead forms.
    </div>
  </>;
}
